import { NumericFilterCondition } from '@/types';

/**
 * Returns true if the numeric condition has any active constraint (would filter rows).
 */
export const isNumericConditionActive = (cond: NumericFilterCondition): boolean => {
  if (!cond) return false;
  switch (cond.operator) {
    case 'isEmpty':
    case 'isNotEmpty':
      return true;
    case 'between':
      return cond.value1 !== null || cond.value2 !== null;
    case 'lessThan':
    case 'greaterThan':
    case 'equals':
    case 'notEqual':
      return cond.value1 !== null;
    default:
      return false;
  }
};

const safeNum = (v: number | null | undefined): number | null =>
  v != null && !Number.isNaN(v) ? v : null;

/**
 * Applies a numeric filter condition to a cell value (number or null/undefined).
 * Returns true if the row passes the filter.
 */
export const applyNumericCondition = (
  value: number | null | undefined,
  cond: NumericFilterCondition
): boolean => {
  if (!cond) return true;
  const num = safeNum(value);
  const v1 = safeNum(cond.value1);
  const v2 = safeNum(cond.value2);
  switch (cond.operator) {
    case 'isEmpty':
      return num === null || num === undefined || Number.isNaN(value as number);
    case 'isNotEmpty':
      return num !== null && num !== undefined && !Number.isNaN(value as number);
    case 'between': {
      if (num === null) return false;
      const low = v1 !== null ? v1 : -Infinity;
      const high = v2 !== null ? v2 : Infinity;
      return num >= low && num <= high;
    }
    case 'lessThan':
      if (num === null) return false;
      return v1 !== null && num < v1;
    case 'greaterThan':
      if (num === null) return false;
      return v1 !== null && num > v1;
    case 'equals':
      if (v1 === null) return num === null;
      return num !== null && num === v1;
    case 'notEqual':
      if (v1 === null) return num !== null;
      return num !== v1;
    default:
      return true;
  }
};
